// --- src/AppContext.js ---
import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { artifact1DataRevised } from './artifact/artifact1DataRevised';
import { artifact2DataRevised } from './artifact/artifact2DataRevised';

const AppContext = createContext();

const AppContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const blockRefs = useRef({});
  const [scrollTo, setScrollTo] = useState(null);
  const [scrollPositions, setScrollPositions] = useState({});
  const [highlightedBlockId, setHighlightedBlockId] = useState(null);
  // REMOVED: highlightTimeoutRef = useRef(null);

  const artifacts = {
    "restaurant-app-market-research": artifact1DataRevised,
    "restaurant-app-requirements": artifact2DataRevised,
  };

  // --- Memoized Functions ---

  const getArtifactById = useCallback((artifactId) => artifacts[artifactId], []);

  const findBlockById = useCallback((artifactId, blockId) => {
    const artifact = getArtifactById(artifactId);
    if (!artifact) return null;
    const findBlock = (blocks, targetId) => {
      for (const block of blocks) {
        if (block.id === targetId) return block;
        if (block.children) {
          const found = findBlock(block.children, targetId);
          if (found) return found;
        }
        if (block.type === 'lens' && block.result) {
          if (block.result.id === targetId) return block.result;
          if (block.result.children) {
            const foundInResult = findBlock(block.result.children, targetId);
            if (foundInResult) return foundInResult;
          }
        }
      }
      return null;
    };
    return findBlock(artifact.children, blockId);
  }, [getArtifactById]);

  const updateScrollPosition = useCallback((artifactId) => {
    if (artifactId) {
      const currentScrollY = window.scrollY;
      setScrollPositions(prev => {
        if (prev[artifactId] !== currentScrollY) {
            // console.log(`Saving scroll position ${currentScrollY} for ${artifactId}`);
            return { ...prev, [artifactId]: currentScrollY };
        }
        return prev;
      });
    }
  }, []);

  const getScrollPosition = useCallback((artifactId) => {
    return scrollPositions[artifactId] || 0;
  }, [scrollPositions]);

  // *** MODIFIED: performScrollToBlock accounts for desktop command panel ***
  const performScrollToBlock = useCallback((targetBlockIdWithPrefix) => {
    console.log(`Attempting to scroll to and highlight: ${targetBlockIdWithPrefix}`);
    // REMOVED: Timeout clearing logic

    if (blockRefs.current[targetBlockIdWithPrefix]) {
      const targetElement = blockRefs.current[targetBlockIdWithPrefix];

      // *** MODIFIED START: Account for desktop command panel height (task pm_task_scroll_cmd_panel_fix_001) ***
      const isDesktop = window.innerWidth >= 1024;
      const commandPanelHeight = 53; // Height of the fixed command panel (from pm_task_cmd_center_ui_001)
      const mobileHeaderHeight = 58; // Assumed height of the mobile fixed header (from pm_013 context)

      // Calculate header offset based on viewport width
      let headerHeight = 0;
      if (!isDesktop) { // Mobile view
          headerHeight = mobileHeaderHeight;
      } else { // Desktop view (>= 1024px)
          headerHeight = commandPanelHeight;
      }
      // *** MODIFIED END ***

      const targetTop = targetElement.getBoundingClientRect().top + window.scrollY;
      const desiredGap = 5; // Small gap above the target block
      const scrollPosition = Math.max(0, targetTop - headerHeight - desiredGap);

      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      console.log(`Scrolled to ${targetBlockIdWithPrefix} at position ${scrollPosition} (offset: ${headerHeight}px + gap: ${desiredGap}px)`);

      // Set the highlight state - Block.js will handle clearing it via animationend
      setHighlightedBlockId(targetBlockIdWithPrefix);

      // REMOVED: setTimeout logic to clear highlight

      setScrollTo(null); // Clear post-navigation scroll target
    } else {
      console.error(`Target element not found in blockRefs for ID: ${targetBlockIdWithPrefix}`);
       // Ensure highlight is cleared if target not found (or never set)
       setHighlightedBlockId(null);
    }
    // Ensure dependencies reflect usage; setHighlightedBlockId is used.
  }, [blockRefs, setScrollTo, setHighlightedBlockId]);

  const navigateToBlock = useCallback((artifactId, blockIdWithoutPrefix) => {
    const currentArtifactId = location.pathname.split('/artifact/')[1];
    const fullBlockIdWithPrefix = `block-${blockIdWithoutPrefix}`;
    if (artifactId === currentArtifactId) {
      performScrollToBlock(fullBlockIdWithPrefix);
    } else {
      updateScrollPosition(currentArtifactId);
      navigate(`/artifact/${artifactId}`, { state: { scrollTo: fullBlockIdWithPrefix } });
    }
  }, [location.pathname, performScrollToBlock, updateScrollPosition, navigate]);

  // --- End Memoized Functions ---

  const menuConfig = Object.entries(artifacts).map(([id, artifact]) => ({
    id,
    label: artifact.name,
    path: `/artifact/${id}`,
  }));

  // *** MODIFIED: Add setHighlightedBlockId to context value ***
  const contextValue = {
    menuConfig,
    getArtifactById,
    artifacts,
    findBlockById,
    navigateToBlock,
    performScrollToBlock,
    scrollTo,
    setScrollTo,
    blockRefs,
    updateScrollPosition,
    getScrollPosition,
    highlightedBlockId,
    setHighlightedBlockId, // Expose setter for Block component
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => {
  return useContext(AppContext);
};

export { AppContext, AppContextProvider, useAppContext };